import { HttpStatusCode } from 'axios'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { IBookingScheduler } from '../../models'
import { BookingSchedulerService, LiteralsService } from '../../services'
import {
  addBookingSchedulerSuccessfulAction,
  editBookingSchedulerSuccessfulAction,
  fetchBookingSchedulersAction,
  fetchBookingSchedulersSuccessfulAction,
  isAddingBookingSchedulerAction,
  isEditingBookingSchedulerAction,
  isFetchingBookingSchedulersAction,
  isRemovingBookingSchedulerAction,
  removeBookingSchedulerSuccessfulAction,
  setNotificationAction
} from '../actions'
import { IStoreState } from '../states'
import { BookingSchedulerTypeKeys } from '../types'

function* fetchBookingSchedulersAsync(): any {
  let bookingSchedulers: IBookingScheduler[] = []
  yield put(isFetchingBookingSchedulersAction())
  try {
    bookingSchedulers = yield call(BookingSchedulerService.getBookingSchedulers)
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
  yield put(fetchBookingSchedulersSuccessfulAction(bookingSchedulers))
}

function* addBookingSchedulerAsync(action: any): any {
  try {
    yield put(isAddingBookingSchedulerAction())
    yield call(BookingSchedulerService.addBookingScheduler, action.payload)
    yield put(
      setNotificationAction({
        isError: false,
        label: LiteralsService.get('bookingSchedulerCreated', true)
      })
    )
    yield put(fetchBookingSchedulersAction())
  } catch (error: any) {
    const message = error?.message ?? ''
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get(
          message.includes(HttpStatusCode.NotFound) ? 'notFoundBookingSchedulerForCreation'
          : message.includes(HttpStatusCode.InternalServerError) ? 'errorCreatingBookingScheduler'
          : message.includes(HttpStatusCode.Unauthorized) ? 'favoriteUnauthorized'
          : message.includes(HttpStatusCode.Conflict) ? 'alreadyExistsBookingScheduler'
          : 'serverError',
          true
        )
      })
    )
  } finally {
    yield put(addBookingSchedulerSuccessfulAction())
  }
}

function* editBookingSchedulerAsync(action: any): any {
  try {
    yield put(isEditingBookingSchedulerAction())
    yield call(BookingSchedulerService.editBookingScheduler, action.payload)
    yield put(
      setNotificationAction({
        isError: false,
        label: LiteralsService.get('bookingSchedulerEdited', true)
      })
    )
    yield put(fetchBookingSchedulersAction())
  } catch (error: any) {
    const message = error?.message ?? ''
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get(
          message.includes(HttpStatusCode.NotFound) ? 'notFoundBookingSchedulerForEdit'
          : message.includes(HttpStatusCode.InternalServerError) ? 'errorEditingBookingScheduler'
          : message.includes(HttpStatusCode.Unauthorized) ? 'favoriteUnauthorized'
          : 'serverError',
          true
        )
      })
    )
  } finally {
    yield put(editBookingSchedulerSuccessfulAction())
  }
}

function* removeBookingSchedulerAsync(): any {
  try {
    yield put(isRemovingBookingSchedulerAction())
    const selected = yield select((state: IStoreState) => state.bookingScheduler.selectedBookingScheduler)
    yield call(BookingSchedulerService.removeBookingScheduler, selected.id)
    yield put(
      setNotificationAction({
        isError: false,
        label: LiteralsService.get('bookingSchedulerDeleted', true)
      })
    )
    yield put(fetchBookingSchedulersAction())
  } catch (error: any) {
    const message = error?.message ?? ''
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get(
          message.includes(HttpStatusCode.NotFound) ? 'notFoundBookingSchedulerForDelete'
          : message.includes(HttpStatusCode.InternalServerError) ? 'errorDeletingBookingScheduler'
          : 'serverError',
          true
        )
      })
    )
  } finally {
    yield put(removeBookingSchedulerSuccessfulAction())
  }
}

function* watchFetchBookingSchedulersAsync() {
  yield takeLatest(BookingSchedulerTypeKeys.FETCH_BOOKING_SCHEDULERS, fetchBookingSchedulersAsync)
}

function* watchAddBookingSchedulerAsync() {
  yield takeLatest(BookingSchedulerTypeKeys.ADD_BOOKING_SCHEDULER, addBookingSchedulerAsync)
}

function* watchEditBookingSchedulerAsync() {
  yield takeLatest(BookingSchedulerTypeKeys.EDIT_BOOKING_SCHEDULER, editBookingSchedulerAsync)
}

function* watchRemoveBookingSchedulerAsync() {
  yield takeLatest(BookingSchedulerTypeKeys.REMOVE_BOOKING_SCHEDULER, removeBookingSchedulerAsync)
}

export default function* rootSaga() {
  yield all([
    watchFetchBookingSchedulersAsync(),
    watchAddBookingSchedulerAsync(),
    watchEditBookingSchedulerAsync(),
    watchRemoveBookingSchedulerAsync()
  ])
}
